import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AttachmentPickerProviderInfo } from '@reflex/plugin-api';
import { useAuth } from '@/hooks/auth/useAuth';
import { useProviderConnect } from '@/hooks/auth/useProviderConnect';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Everything an attachment picker needs to gate its options on OAuth state,
 * in the shape `PluginAttachmentPickerProps` declares.
 */
export interface AttachmentPickerProviders {
  providers: AttachmentPickerProviderInfo[];
  connectProvider: (provider: string) => Promise<void>;
  connectingProvider: string | null;
}

/**
 * Resolves the OAuth provider state attachment pickers gate on: which
 * providers the host has configured (admin-level) and which the current user
 * has connected (user-level), plus the connect handler.
 *
 * Every surface that hosts a plugin picker needs this, so it lives here rather
 * than in one of them. Without it a picker sees an empty provider list, and
 * `classifyMcpConfigs` treats unrecognised providers as operator-managed and
 * passes them through as `'ready'` — so an OAuth server the user has not
 * connected would look attachable and fail at agent start instead of offering
 * a Connect button.
 */
export function useAttachmentPickerProviders(): AttachmentPickerProviders {
  const { providers: configuredProviders } = useAuth();
  const { connect, connectingProvider } = useProviderConnect();
  const providersQuery = useQuery({
    queryKey: queryKeys.userProviders,
    queryFn: api.getUserProviders,
  });

  const providers = useMemo<AttachmentPickerProviderInfo[]>(() => {
    const linkedSet = new Set((providersQuery.data ?? []).map((p) => p.provider as string));
    return Object.entries(configuredProviders).map(([provider]) => ({
      provider,
      configured: true,
      connected: linkedSet.has(provider),
    }));
  }, [configuredProviders, providersQuery.data]);

  const connectProvider = useCallback(
    async (provider: string) => {
      await connect(provider);
    },
    [connect],
  );

  return { providers, connectProvider, connectingProvider };
}
